import { Button, useMediaQuery, useToast, Box } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import FormField from 'components/FormField';
import { ChangePassword, SetAuthPageType } from 'redux/AuthSlice';
import CustomToast from 'components/CustomToast';
import { ChangePasswordSchema } from 'validation/schemas/AuthenticationSchema';

import type { ReactElement, ComponentType } from 'react';

const ChakraSubmitButton = Button as ComponentType<any>;

type ChangePasswordFormValues = {
  password: string;
  confirmPassword: string;
};

const initialValues: ChangePasswordFormValues = {
  password: '',
  confirmPassword: '',
};

const PasswordRulesChecklist = ({
  password,
  submitCount,
}: {
  password: string;
  submitCount: number;
}) => {
  const passwordRules = [
    { label: 'Minimum 6 characters', isMet: password.length >= 6 },
    { label: 'At least 1 number', isMet: /\d/.test(password) },
    { label: 'At least 1 uppercase letter', isMet: /[A-Z]/.test(password) },
    { label: 'At least 1 lowercase letter', isMet: /[a-z]/.test(password) },
    {
      label: 'At least 1 symbol',
      isMet: /[^\dA-Za-z]/.test(password),
    },
  ];
  const showUnmetPasswordRulesInRed = submitCount > 0;

  return (
    <Box mt='2'>
      {passwordRules.map((rule) => {
        const ruleColor = rule.isMet
          ? '#2E7D32'
          : showUnmetPasswordRulesInRed
          ? '#D32F2F'
          : '#82736C';
        return (
          <Box
            as='p'
            key={rule.label}
            fontSize='12px'
            fontWeight='500'
            sx={{ color: ruleColor }}
            m={0}
          >
            {rule.isMet ? '✓' : '○'} {rule.label}
          </Box>
        );
      })}
    </Box>
  );
};

const ChangePasswordMobileSubmit = ({
  navigate,
  dispatch,
}: {
  navigate: ReturnType<typeof useNavigate>;
  dispatch: any;
}): ReactElement => (
  <Box width='100%'>
    <ChakraSubmitButton
      mt='1rem'
      type='submit'
      w='100%'
      h='9'
      borderRadius='xs'
      fontSize='xs'
      bgColor='green.900'
    >
      Continue
    </ChakraSubmitButton>
    <small
      style={{
        width: '100%',
        cursor: 'pointer',
        color: '#939393',
        textAlign: 'center',
        margin: '.7rem 0 0 0',
        textDecoration: 'underline',
      }}
      onClick={() => {
        navigate('/');
        dispatch(SetAuthPageType(1));
      }}
    >
      Back to login
    </small>
  </Box>
);

type ChangePasswordFormFieldsProperties = {
  errors: Partial<Record<keyof ChangePasswordFormValues, string | undefined>>;
  touched: Partial<Record<keyof ChangePasswordFormValues, boolean | undefined>>;
  values: ChangePasswordFormValues;
  submitCount: number;
  isMobile: boolean;
  navigate: ReturnType<typeof useNavigate>;
  dispatch: any;
};

const ChangePasswordFormFields = ({
  errors,
  touched,
  values,
  submitCount,
  isMobile,
  navigate,
  dispatch,
}: ChangePasswordFormFieldsProperties): ReactElement => (
  <Form>
    <Box display='flex' flexWrap='wrap' gap='7' alignItems='center'>
      <FormField
        label='New Password:'
        name='password'
        type='password'
        labelStyles={{
          fontSize: '14px',
          color: '#121826',
        }}
        inputStyles={{
          maxWidth: '300px',
          boxShadow: '0px 0px 0px 1px #7A849E',
        }}
        placeholder='************'
        error={errors.password}
        touched={touched.password}
      >
        <Box width={'150px'}></Box>
      </FormField>
      <FormField
        label='Confirm New Password:'
        name='confirmPassword'
        type='password'
        labelStyles={{
          fontSize: '14px',
          color: '#121826',
        }}
        inputStyles={{
          maxWidth: '300px',
          boxShadow: '0px 0px 0px 1px #7A849E',
        }}
        styles={{
          marginTop: '.7rem',
        }}
        placeholder='************'
        error={errors.confirmPassword}
        touched={touched.confirmPassword}
      >
        <Box display={'flex'} flexDirection={'column'}>
          <ChakraSubmitButton
            type='submit'
            w='150px'
            h='9'
            borderRadius='xs'
            fontSize='xs'
            bgColor='green.900'
          >
            Continue
          </ChakraSubmitButton>
          <small
            style={{
              width: '100%',
              textAlign: 'center',
              marginTop: '.7rem',
              cursor: 'pointer',
              color: '#939393',
              textDecoration: 'underline',
            }}
            onClick={() => {
              navigate('/');
              dispatch(SetAuthPageType(1));
            }}
          >
            Back to login
          </small>
        </Box>
      </FormField>
      {isMobile && <ChangePasswordMobileSubmit navigate={navigate} dispatch={dispatch} />}
    </Box>
    <PasswordRulesChecklist password={values.password} submitCount={submitCount} />
  </Form>
);

const ChangePasswordForm = () => {
  const [isMobile] = useMediaQuery('(max-width: 576px)');
  const [queryParameters] = useSearchParams();
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();

  const onSubmitForm = (form: any) => {
    dispatch(
      ChangePassword({
        request_body: {
          token: queryParameters.get('token'),
          password: form.password,
        },
      }),
    ).then((response: any) => {
      if (response.type.endsWith('/fulfilled')) {
        toast({
          duration: 2500,
          render: () => <CustomToast title={response?.payload.message} status='success' />,
        });
        localStorage.removeItem('BLOX_PASSWORD_TOKEN');
        navigate('/');
      } else {
        toast({
          duration: 2500,
          render: () => (
            <CustomToast
              title={response?.payload?.message || 'Something went wrong..'}
              status='error'
            />
          ),
        });
      }
    });
  };
  return (
    <Formik<ChangePasswordFormValues>
      initialValues={initialValues}
      onSubmit={onSubmitForm}
      validationSchema={ChangePasswordSchema}
    >
      {(properties) => {
        const { errors, touched, values, submitCount } = properties as {
          errors: Partial<Record<keyof ChangePasswordFormValues, string | undefined>>;
          touched: Partial<Record<keyof ChangePasswordFormValues, boolean | undefined>>;
          values: ChangePasswordFormValues;
          submitCount: number;
        };
        return (
          <ChangePasswordFormFields
            errors={errors}
            touched={touched}
            values={values}
            submitCount={submitCount}
            isMobile={isMobile}
            navigate={navigate}
            dispatch={dispatch}
          />
        );
      }}
    </Formik>
  );
};

export default ChangePasswordForm;
